import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {EditPermissions, NodeBuilder, NodeFlowDetails} from '../../../../../../../shared/models';
import { UntypedFormArray, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import {ChatHubNode} from './chatHub';
import {
    isValidVariable,
    maximumCount,
    minimumCount,
    regExpValidation,
    required
} from '../../../../../../../shared/utils';
import {ChatHubService} from '../../../../../../../core/services/chathub/chat-hub.service';

import {catchError, map, take, tap} from 'rxjs/operators';
import {map as _map} from 'lodash';
import filter from 'lodash/filter';
import find from 'lodash/find';
import last from 'lodash/last';
import uniqBy from 'lodash/uniqBy';
import indexOf from 'lodash/indexOf';
import values from 'lodash/values';
import sortBy from 'lodash/sortBy';
import {swalMini} from '../../../../../../../shared/utils/utils';
import {throwError} from 'rxjs';
import {FlowBuilderService} from '../../../../../services/flowBuilder/flow-builder.service';
import { ClickatellExpansionPanelComponent } from '../../../../../../../shared/components/clickatell-expansion-panel/clickatell-expansion-panel.component';
import { MatExpansionPanelTitle, MatAccordion, MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';
import { ActiveFormDirective } from '../../../../../../../shared/directives/active-form/active-form.directive';
import { InputValidatorsComponent } from '../../templates/input-validators/input-validators.component';
import { MatSelect, MatOption } from '@angular/material/select';
import { LoadingIconComponent } from '../../../../../../../shared/components/loading-icon/loading-icon.component';
import { NgTemplateOutlet } from '@angular/common';
import { CMSComponent } from '../../../../../../../shared/components/cms/cms.component';
import { NextSelectorComponent } from '../../../../json-to-from/next-selector/next-selector.component';
import { NumberSelectorInputComponent } from '../../../../../../../shared/components/form-controls/number-selector-input/number-selector-input.component';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MultiSelectDropdownComponent } from '../../../../../../../shared/components/form-controls/multi-select-dropdown/multi-select-dropdown.component';
import { OverflowTooltipDirective } from '../../../../../../../shared/directives/overflow-tooltip/overflow-tooltip.directive';
import { MatTooltip } from '@angular/material/tooltip';

const NODE_TYPE = 'chatHub';

interface Instance {
    id: string;
    displayName: string;
}

interface APIProvider {
    id: string;
    name: string;
}

interface ChatHubParameter {
    id: string;
    label: string | null;
    default: string | null;
    required: boolean;
    constraints: [] | null;
    placeholder: string | null;
    parameterType: string;
    presentationType: string;
    availableOptions: [] | null;
    repeatableGroupId: number;
    repeatableGroupName: string | null;
    isArray: boolean;
}

interface ChatHubGroup {
    id: number;
    name: string;
    items: ChatHubGroupItem[];
}

interface ChatHubGroupItem {
    id: number;
    parameters: ChatHubParameter[];
}

@Component({
    selector: 'chat-hub',
    templateUrl: './chat-hub.component.html',
    styleUrls: ['./chat-hub.component.scss'],
    imports: [ClickatellExpansionPanelComponent, MatExpansionPanelTitle, ReactiveFormsModule, ActiveFormDirective, InputValidatorsComponent, MatSelect, MatOption, LoadingIconComponent, NgTemplateOutlet, CMSComponent, NextSelectorComponent, NumberSelectorInputComponent, MatSlideToggle, MultiSelectDropdownComponent, MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, OverflowTooltipDirective, MatTooltip]
})

export class ChatHubComponent extends NodeBuilder implements OnInit, OnDestroy {

    constructor(private chatHubService: ChatHubService, private flowBuilder: FlowBuilderService) {
        super(NODE_TYPE);
    }

    actionParams: UntypedFormGroup = undefined;
    chatHubParameters: UntypedFormArray = undefined;
    canEdit: EditPermissions;
    instances: Instance[] = [];
    apiProviders: APIProvider[] = [];
    gettingApiProvidersInProgress: Boolean = false;
    gettingNodeParametersInProgress: Boolean = false;
    gettingNodeParametersError: Boolean = false;
    chatHubParametersData: any[] = null;
    private providerId: string;
    chatHubGroups: UntypedFormArray = null;
    chatHubGroupsData: any[] = null;
    groupsItemsTemplate: any[] = [];
    stepUniqueID = '';
    nodeFlowDetails: NodeFlowDetails = {
        flows: undefined,
        currentFlowSteps: undefined,
        extraFields: {},
        stepId: ''
    };

    @Input() stepId: string;

    ngOnInit(): void {

    }

    ngOnDestroy() {
        const actionParams = this.actionParams.getRawValue();
        const parameters = this.chatHubParameters.getRawValue();
        const groups = this.chatHubGroups.getRawValue();

        if (parameters && parameters.length !== 0) {
            const chatHubData = this.chatHubService.getChatHubData();
            const stepIndex = indexOf(chatHubData, {step: this.stepId});
            if (stepIndex !== -1) {
                chatHubData[stepIndex] = {actionParams, parameters, groups, step: this.stepId};
            } else {
                chatHubData.push({actionParams, parameters, groups, step: this.stepId});
            }
            this.chatHubService.setChatHubData(chatHubData);
        }
    }

    set buildForm(stepData: any) {
        this.stepUniqueID = stepData.metadata?.stepUniqueID ? stepData.metadata?.stepUniqueID : '';
        const formData = ChatHubNode.toForm(stepData);
        if (!this.nodeFlowDetails.extraFields || !this.nodeFlowDetails.extraFields['providerId']) {
            this.providerId = formData.actionParams ? formData.actionParams['providerId'] : null;
            this.rebuildNode(formData);
        } else {
            this.providerId = this.nodeFlowDetails.extraFields['providerId'];
            this.buildNewNode(formData);
        }

        if (this.providerId) {
            this.actionParams.get('providerId').setValue(this.providerId);
            this.getInstances();
            this.getApiProviders();
        }
    }

    private buildNewNode(formData): void {
        this.buildActionParamsForm(formData);
        this.buildParametersForm(formData);
        this.buildGroupsForm(formData);
    }

    private rebuildNode(formData): void {
        this.chatHubParameters = ChatHubNode.getParametersTemplate();
        this.chatHubGroups = ChatHubNode.getGroupsTemplate();
        this.buildActionParamsForm(formData);
        if (formData.actionParams['apiId']) {
            this.getNodeParametersAndGroups(formData.actionParams['apiId'], formData);
        }
    }

    buildActionParamsForm(formData) {
        const imageUrl = this.nodeFlowDetails.extraFields ? this.nodeFlowDetails.extraFields['imageUrl'] : null;
        if (imageUrl) {
            formData.actionParams.imageUrl = imageUrl;
        }
        this.actionParams = ChatHubNode.getActionTemplate(this.canEdit);
        this.actionParams.patchValue(formData.actionParams);
    }

    buildParametersForm(formData) {
        this.chatHubParameters = ChatHubNode.getParametersTemplate();
        for (const key in formData.parameters) {
            this.addParameter();
        }
        this.chatHubParameters.patchValue(formData.parameters);
    }

    buildGroupsForm(formData) {
        this.chatHubGroups = ChatHubNode.getGroupsTemplate();
        for (const key in formData.groups) {
            this.addGroup(formData.groups[key]);
        }
        this.chatHubGroups.patchValue(formData.groups);
    }

    get getStepData(): {} {
        const actionParams = this.actionParams.getRawValue();
        let parameters = this.chatHubParameters.getRawValue();
        let groups = this.chatHubGroups.getRawValue();

        if (!parameters || parameters.length === 0) {
            const chatHubData = this.chatHubService.getChatHubData();
            if (chatHubData && chatHubData.length !== 0) {
                const currChatHubData = find(chatHubData, {step: this.stepId});
                if (currChatHubData) {
                    parameters = currChatHubData.parameters;
                    groups = currChatHubData.groups;
                }
            }
        }

        const chatHubNode = new ChatHubNode(this.nodeType);
        chatHubNode.addChatHubParams(this.providerId, actionParams, parameters, groups);
        chatHubNode.addActionParams(actionParams.name, actionParams.description);
        chatHubNode.addResult(actionParams.success, actionParams.error);
        const nodeData = chatHubNode.nodeData;
        if (this.stepUniqueID !== '') {
            nodeData['metadata'].stepUniqueID = this.stepUniqueID;
        }
        return nodeData;
    }

    get isNodeValid(): boolean {
        this.actionParams.markAllAsTouched();
        this.chatHubParameters.markAllAsTouched();
        this.chatHubGroups.markAllAsTouched();
        const currValue = this.chatHubGroups.getRawValue();
        this.chatHubGroups.patchValue(currValue);

        return this.actionParams.valid && this.chatHubParameters.valid && this.chatHubGroups.valid;
    }

    getInstances() {
        this.chatHubService.getProviderInstances(this.providerId).pipe(
            take(1),
            tap((instances: Instance[]) => this.instances = instances),
            tap((instances: Instance[]) => {
                if (instances.length === 1) {
                    this.actionParams.get('instanceId').setValue(instances[0].id);
                }
            })
        ).subscribe();
    }

    getApiProviders() {
        this.gettingApiProvidersInProgress = true;
        this.chatHubService.getProviderApis(this.providerId).pipe(
            catchError(error => {
                this.apiProviders = null;
                return throwError(error);
            }),
            take(1),
            tap((providers: APIProvider[]) => this.apiProviders = (providers && providers.length > 0) ? this.sortProviders(providers) : null),
            tap(() => {
                this.gettingApiProvidersInProgress = false;
                this.flowBuilder.setNodeValid(this.nodeFlowDetails.stepId, this.isNodeValid);
            })
        ).subscribe();
    }

    sortProviders(providers) {
        return sortBy(providers, ['name']);
    }

    getNodeParametersAndGroups(apiId: string, data?: any) {
        this.gettingNodeParametersInProgress = true;

        this.chatHubService.getProviderApiDetails(this.providerId, apiId).pipe(
            catchError(err => {
                this.gettingNodeParametersInProgress = false;
                this.gettingNodeParametersError = true;
                return throwError(err);
            }),
            map(resp => {
                if (resp && resp.requestParameters) {
                    const parameters = resp.requestParameters;
                    const filteredParameters = this.filterParametersToGroups(parameters);
                    const parametersData = this.buildParametersData(filteredParameters.parameters);
                    const groupsData = this.buildGroupsData(filteredParameters.groups);
                    this.gettingNodeParametersInProgress = false;
                    return {
                        parameters: parametersData,
                        groups: groupsData
                    };
                }
                this.gettingNodeParametersInProgress = false;
                return;
            }),
            tap(parametersData => {
                if (data && data.parameters.length !== 0 && data.groups.length !== 0) {
                    this.chatHubParameters.patchValue(data.parameters);
                    this.setParametersToGroups(data.groups);
                } else {
                    this.chatHubParameters.patchValue(parametersData.parameters);
                }
            })
        ).subscribe();
    }

    private filterParametersToGroups(parameters: ChatHubParameter[]) {
        parameters = uniqBy(parameters, obj => obj.id);
        parameters = parameters.map(x => {
            const placeholder: string = x.placeholder === null ? '' : x.placeholder;
            return {...x, placeholder};
        });
        const filteredParameters = {
            parameters: [],
            groups: []
        };
        const repeatableGroupIds = Array.from(parameters, ({repeatableGroupId}) => repeatableGroupId);
        const uniqueRepeatableGroupIds = [...new Set(repeatableGroupIds)];

        _map(uniqueRepeatableGroupIds, (item) => {
            (item === null)
                ? filteredParameters.parameters = filter(parameters, {repeatableGroupId: null})
                : filteredParameters.groups.push({
                    id: item,
                    name: this.setGroupName(parameters, item),
                    items: [
                        {
                            id: `${this.setGroupName(parameters, item)} 1`,
                            parameters: filter(parameters, {repeatableGroupId: item})
                        }
                    ]
                });
        });

        _map(filteredParameters.groups, (group) => {
            this.groupsItemsTemplate.push({...group});
            const hasRequiredOptions = this.checkRequiredGroupItem(group.items[0]);
            if (!hasRequiredOptions) {
                group.items = [];
            }
        });

        return filteredParameters;
    }

    setGroupName(parameters, item) {
        const parameter = find(parameters, {repeatableGroupId: item});
        if (parameter && parameter.hasOwnProperty('repeatableGroupName') && parameter.repeatableGroupName !== null) {
            return parameter.repeatableGroupName;
        }
        return '';
    }

    private buildGroupsData(groups: ChatHubGroup[]) {
        if (this.chatHubGroups.controls.length !== 0) {
            while (this.chatHubGroups.length !== 0) {
                this.chatHubGroups.removeAt(0);
            }
        }
        this.chatHubGroupsData = groups;
        return _map(groups, (item) => {
            return this.addGroup(item);
        });
    }

    addGroup(group: ChatHubGroup) {
        const groupTemplate = ChatHubNode.getGroupTemplate();
        groupTemplate.controls.items = ChatHubNode.getGroupItemsTemplate();
        const groupItems = groupTemplate.controls.items as UntypedFormArray;
        const mappedGroupItems = _map(group.items, (item, index) => {
            groupItems.push(ChatHubNode.getGroupItemTemplate());
            const itemForm = groupItems.controls[index] as UntypedFormGroup;
            const paramsForm = itemForm.controls.parameters as UntypedFormArray;
            const parameters = _map(item.parameters, (parameter, parameterIndex) => {
                paramsForm.push(ChatHubNode.getParameterTemplate());
                const parameterControls = paramsForm.controls[parameterIndex] as UntypedFormGroup;
                this.setValidators(parameterControls, parameter);
                return this.buildParameterData(parameter);
            });
            return {...item, parameters};
        });
        groupTemplate.patchValue({...group, items: mappedGroupItems});
        this.chatHubGroups.push(groupTemplate);
        return {...group, items: mappedGroupItems};
    }

    private buildParametersData(parameters: ChatHubParameter[]) {
        if (this.chatHubParameters.controls.length !== 0) {
            while (this.chatHubParameters.length !== 0) {
                this.chatHubParameters.removeAt(0);
            }
        }
        this.chatHubParametersData = parameters;
        return _map(parameters, (param, index) => {
            if (param['isArray']) {
                this.addArrayParameter();
            } else {
                this.addParameter();
            }
            const parameterControls = this.chatHubParameters.controls[index] as UntypedFormGroup;
            this.setValidators(parameterControls, param);
            return this.buildParameterData(param);
        });
    }

    private buildParameterData(parameter: ChatHubParameter) {
        return ({
            id: parameter.id,
            type: parameter.parameterType,
            value: this.setValueOrDefault(parameter),
            isArray: parameter.isArray,
            required: parameter.required
        });
    }

    private setValueOrDefault(parameter: ChatHubParameter) {
        if (parameter.default === null) {
            if (!parameter.isArray && parameter.parameterType === 'BOOLEAN') {
                return false;
            }
            if (parameter.isArray) {
                return [];
            }
            return null;
        }
        return parameter.default;
    }

    addParameter() {
        this.chatHubParameters.push(ChatHubNode.getParameterTemplate());
    }

    addArrayParameter() {
        this.chatHubParameters.push(ChatHubNode.getParameterTemplateArrayValue());
    }

    setValidators(parameter, parameterData) {
        const validators: any = this.checkValidators(parameterData);
        parameter.controls['value'].setValidators(validators);
    }

    private checkValidators(parameter) {
        const validators: any[] = [];
        if (parameter.required) {
            validators.push(required('Value'));
        }
        if (parameter.constraints) {
            parameter.constraints.map(constraint => {
                if (constraint.type === 'MIN') {
                    validators.push(minimumCount(+constraint.value, 'Value'));
                }
                if (constraint.type === 'MAX') {
                    validators.push(maximumCount(+constraint.value, 'Value'));
                }
                if (constraint.constraintType === 'REGEXP') {
                    validators.push(regExpValidation(constraint.value, 'Value'));
                }
            });
        }
        if (parameter.parameterType === 'FILE') {
            validators.push(isValidVariable());
        }

        return validators;
    }

    setParametersToGroups(groupsData) {
        const groups = this.chatHubGroups.getRawValue();
        _map(this.groupsItemsTemplate, (group, i) => {
            const items = [];
            _map(group.items[0].parameters, parameter => {
                const paramsArr = values(groupsData[parameter.id]);
                _map(paramsArr, (paramItem, index) => {
                    const itemIdArr = group.items[0].id.split(' ');
                    itemIdArr.pop();
                    const id = itemIdArr.join(' ') + ' ' + (index + 1);
                    const param = {
                        id: parameter.id,
                        ...paramItem
                    };
                    if (!items[index]) {
                        items.push({
                            id,
                            parameters: [param]
                        });
                    } else {
                        items[index].parameters.push(param);
                    }
                });
            });

            groups[i].items = items;
        });
        this.patchChatHubGroups(groups);
    }

    patchChatHubGroups(groupsData) {
        if (groupsData && groupsData.length !== 0) {
            _map(this.chatHubGroups.controls, (group, groupIndex) => {
                groupsData[groupIndex].name = this.chatHubGroupsData[groupIndex].name;
                const items = group.controls.items as UntypedFormArray;
                const chatHubGroupsDataItems = this.chatHubGroupsData[groupIndex].items;

                if (items.controls.length !== 0) {
                    while (items.length !== 0) {
                        items.removeAt(0);
                    }
                }
                if (items.controls.length !== 0) {
                    while (chatHubGroupsDataItems.length !== 0) {
                        chatHubGroupsDataItems.removeAt(0);
                    }
                }

                _map(groupsData[groupIndex].items, (item, index) => {
                    item.id = groupsData[groupIndex].name + ' ' + (index + 1);
                    this.addGroupItem(group);
                });
            });

            this.chatHubGroups.patchValue(groupsData);
        }
    }

    addGroupItem(form) {
        const groupData = find(this.chatHubGroupsData, {id: form.value.id});
        const groupTemplate = find(this.groupsItemsTemplate, {id: form.value.id});
        const itemData = {...groupTemplate.items[0]};
        itemData.id = form.value.name + ' ' + (groupData.items.length + 1);
        groupData.items.push(itemData);

        const groupItems = form.controls.items as UntypedFormArray;
        groupItems.push(ChatHubNode.getGroupItemTemplate());
        const itemForm = last(groupItems.controls) as UntypedFormGroup;
        const paramsForm = itemForm.controls.parameters as UntypedFormArray;
        const parameters = _map(itemData.parameters, (parameter, parameterIndex) => {
            paramsForm.push(ChatHubNode.getParameterTemplate());
            const parameterControls = paramsForm.controls[parameterIndex] as UntypedFormGroup;
            this.setValidators(parameterControls, parameter);
            return this.buildParameterData(parameter);
        });

        itemForm.patchValue({
            ...itemData,
            parameters
        });
    }

    removeGroupItem(event, form, index) {
        event.stopPropagation();
        const groupDataTemplate = find(this.chatHubGroupsData, {id: form.value.id});
        const item = groupDataTemplate.items[0];
        const hasRequiredOptions = this.checkRequiredGroupItem(item);
        if (form.controls.items.length === 1 && hasRequiredOptions) {
            swalMini({title: 'At least one option needed', type: 'error'});
            return;
        }
        groupDataTemplate.items.splice(index, 1);
        form.controls.items.removeAt(index);
    }

    checkRequiredGroupItem(item) {
        const {parameters} = item;
        if (parameters) {
            return find(parameters, {required: true});
        }
        return;
    }
}
